import type { FilterCatalog, ReportFilterStateV1 } from "@meta-video/contracts";
import { Button, Group } from "@mantine/core";
import { useMemo } from "react";

import { compactList, numericRangeSummary } from "./filterPresentation.js";

export type FilterSection =
  | "accounts"
  | "brands"
  | "date"
  | "duration"
  | "first-seen"
  | "name"
  | "roas"
  | "spend"
  | "subgroups";

export interface ActiveFilterChipsProps {
  readonly catalog: FilterCatalog;
  readonly controls: string;
  readonly filters: ReportFilterStateV1;
  readonly opened: boolean;
  readonly onEdit: (section: FilterSection, trigger: HTMLButtonElement) => void;
}

function firstSeenSummary(
  from: ReportFilterStateV1["firstSeenFrom"],
  to: ReportFilterStateV1["firstSeenTo"],
): string {
  if (from === null && to === null) return "Any date";
  if (from === null) return `Through ${to}`;
  if (to === null) return `From ${from}`;
  return `${from} to ${to}`;
}

function summaries(
  filters: ReportFilterStateV1,
  names: Readonly<{
    readonly accounts: ReadonlyMap<string, string>;
    readonly brands: ReadonlyMap<string, string>;
  }>,
): readonly { readonly label: string; readonly section: FilterSection; readonly value: string }[] {
  const brandNames = filters.brandIds.map((id) => names.brands.get(id) ?? id);
  const accounts = filters.accountIds === null
    ? "All selected-brand accounts"
    : compactList(
        filters.accountIds.map((id) => names.accounts.get(id) ?? id),
        "All selected-brand accounts",
      );
  const date = filters.date.preset === "custom"
    ? `${filters.date.from} to ${filters.date.to}`
    : `Last ${filters.date.preset} days`;
  const subgroupCount = filters.groupSelections.reduce(
    (count, selection) => count + selection.valueIds.length + Number(selection.includeUnassigned),
    0,
  );
  const duration = filters.durationMin === null && filters.durationMax === null
    ? "Any length"
    : `${filters.durationMin ?? 0}–${filters.durationMax ?? "∞"} sec`;
  const firstSeen = firstSeenSummary(
    filters.firstSeenFrom,
    filters.firstSeenTo,
  );

  return [
    { label: "Brands", section: "brands", value: compactList(brandNames, "None") },
    { label: "Accounts", section: "accounts", value: accounts },
    { label: "Date", section: "date", value: date },
    { label: "Subgroups", section: "subgroups", value: subgroupCount === 0 ? "All" : `${subgroupCount} selected` },
    { label: "Duration", section: "duration", value: duration },
    { label: "First seen", section: "first-seen", value: firstSeen },
    { label: "Name", section: "name", value: filters.adNameSearch ?? "Any" },
    { label: "Spend", section: "spend", value: numericRangeSummary(filters.spendMin, filters.spendMax) },
    { label: "ROAS", section: "roas", value: numericRangeSummary(filters.roasMin, filters.roasMax) },
  ];
}

export function ActiveFilterChips({
  catalog,
  controls,
  filters,
  opened,
  onEdit,
}: ActiveFilterChipsProps) {
  const names = useMemo(() => {
    const accounts = new Map<string, string>();
    const brands = new Map<string, string>();
    for (const brand of catalog.brands) {
      brands.set(brand.id, brand.name);
      for (const account of brand.accounts) {
        if (!accounts.has(account.id)) accounts.set(account.id, account.name);
      }
    }
    return { accounts, brands };
  }, [catalog]);

  return (
    <Group aria-label="Active report filters" className="active-filter-chips" gap="xs" role="group">
      {summaries(filters, names).map(({ label, section, value }) => (
        <Button
          aria-controls={controls}
          aria-expanded={opened}
          aria-label={`Edit ${label}: ${value}`}
          className="active-filter-chip"
          key={section}
          onClick={(event) => onEdit(section, event.currentTarget)}
          size="compact-sm"
          variant="default"
        >
          <span className="active-filter-chip__label">{label}</span>
          <span className="active-filter-chip__value">{value}</span>
        </Button>
      ))}
    </Group>
  );
}
